'use client'
import { Box, styled, Typography, Stack, Skeleton, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'

// 1. Định nghĩa Interface cho dữ liệu trả về từ API (Giả định dựa trên query của bạn)
interface IDailySale {
  start_date: string
  end_date?: string
  [key: string]: any
}

// 2. Định nghĩa Type cho Logic hiển thị thời gian để tránh lỗi "possibly undefined"
type TimeDisplayData =
  | { type: 'SAME_DAY'; startTime: string; endTime: string; date: string }
  | {
      type: 'DIFFERENT_DAY'
      startFull: { time: string; date: string }
      endFull: { time: string; date: string }
    }

const TabItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 16px',
  cursor: 'pointer',
  minWidth: '120px',
  color: active ? '#C84B00' : '#FFFFFF',
  transition: 'all 0.2s ease-in-out',
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
  backgroundColor: active ? '#FFFFFF' : 'transparent',
  '&:hover': {
    backgroundColor: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.1)',
  },
  '@media (max-width: 768px)': {
    borderRadius: '0px',
    minWidth: '100px',
    color: active ? '#C84B00' : '#E0E0E0',
  },
}))

interface FlashSaleTabProps {
  sale: IDailySale
  isActive: boolean
  index: number
  isOngoing: boolean
  onClick: () => void
  isLoading?: boolean
}

const FlashSaleTabs = ({ sale, isActive, index, isOngoing, onClick, isLoading }: FlashSaleTabProps) => {
  // Kiểm tra cùng ngày
  const isSameDay =
    sale.end_date && dayjs(sale.start_date).format('DD/MM/YYYY') === dayjs(sale.end_date).format('DD/MM/YYYY')

  // Hàm lấy dữ liệu thời gian (Đã định nghĩa kiểu trả về cụ thể)
  const getTimeData = (): TimeDisplayData => {
    const start = dayjs(sale.start_date)
    const end = dayjs(sale.end_date)

    if (!sale.end_date || isSameDay) {
      return {
        type: 'SAME_DAY',
        startTime: start.format('HH:mm'),
        endTime: end.format('HH:mm'),
        date: start.format('DD/MM/YYYY'),
      }
    }
    return {
      type: 'DIFFERENT_DAY',
      startFull: { time: start.format('HH:mm'), date: start.format('DD/MM/YYYY') },
      endFull: { time: end.format('HH:mm'), date: end.format('DD/MM/YYYY') },
    }
  }

  const timeData = getTimeData()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const getTextColor = (isSecondary = false) => {
    if (isActive) {
      return isSecondary ? 'rgba(200, 75, 0, 0.8)' : '#C84B00'
    }

    if (isMobile) {
      return isSecondary ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'
    }

    return isSecondary ? 'rgba(255, 255, 255, 0.7)' : '#FFFFFF'
  }
  if (isLoading || !sale) {
    return (
      <TabItem sx={{ cursor: 'default' }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Skeleton variant="text" width={60} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
          <Skeleton variant="text" width={60} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
        </Stack>
        <Skeleton variant="text" width={120} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
      </TabItem>
    )
  }
  return (
    <TabItem active={isActive} onClick={onClick}>
      <Stack direction="row" alignItems="center" spacing={0.8}>
        {timeData.type === 'SAME_DAY' ? (
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: getTextColor() }}>
              {timeData.startTime} - {timeData.endTime}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: getTextColor(true) }}>{timeData.date}</Typography>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Cụm Bắt đầu */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.1, color: getTextColor() }}
              >
                {timeData.startFull.time}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: getTextColor(true) }}>
                {timeData.startFull.date}
              </Typography>
            </Box>

            <Typography sx={{ color: getTextColor(true), fontSize: '0.8rem' }}>-</Typography>

            {/* Cụm Kết thúc */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.1, color: getTextColor() }}
              >
                {timeData.endFull.time}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: getTextColor(true) }}>
                {timeData.endFull.date}
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>

      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: isActive ? 600 : 400,
          color: getTextColor(isActive ? false : true),
          marginTop: '4px',
        }}
      >
        {isOngoing ? 'Đang diễn ra' : 'Sắp diễn ra'}
      </Typography>
    </TabItem>
  )
}

export default FlashSaleTabs
